import type { FlowConfigValues } from "../FlowDraftProvider";
import { isAxiosError } from "axios";
import type {
  ConfigField,
  ConfigFieldType,
  DraftEvaluation,
  FlowType,
  MerchantFlowError,
} from "../types";

/** Matches `DISPLAY_ONLY_TYPES` in `apps.merchant_flow.registry`. */
export const DISPLAY_ONLY_TYPES = new Set<ConfigFieldType>([
  "terms_editor",
  "action",
  "refinance_calculator",
]);

export function isInputField(field: Pick<ConfigField, "type">): boolean {
  return !DISPLAY_ONLY_TYPES.has(field.type);
}

/** Input values only — the same set `Defaults.config` carries. First key wins. */
export function inputConfig(dto: DraftEvaluation): FlowConfigValues {
  const config: FlowConfigValues = {};
  for (const field of uniqueSectionFields(dto.sections).flatMap((section) => section.fields)) {
    if (!isInputField(field)) continue;
    config[field.key] = field.value;
  }
  return config;
}

export function uniqueFields(fields: readonly ConfigField[]): ConfigField[] {
  const seen = new Set<string>();
  return fields.filter((field) => {
    if (seen.has(field.key)) return false;
    seen.add(field.key);
    return true;
  });
}

/** Dedup by field key across every section, keeping the first occurrence. */
export function uniqueSectionFields(
  sections: DraftEvaluation["sections"]
): DraftEvaluation["sections"] {
  const seen = new Set<string>();
  return sections.map((section) => ({
    ...section,
    fields: uniqueFields(section.fields).filter((field) => {
      if (seen.has(field.key)) return false;
      seen.add(field.key);
      return true;
    }),
  }));
}

/** The Custom upload pick in the draft (``registry.ONE_OFF_TEMPLATE``). */
export const ONE_OFF_TEMPLATE_KEY = "one_off_template";

const CONTRACT_FIELD_KEYS = new Set([
  "send_main",
  "main_template",
  "contract_template",
  "refinance_template",
  "addendum_contract_template",
  ONE_OFF_TEMPLATE_KEY,
  "document_template",
]);

/** Contract types the send card can switch. Main contract is always listed. */
export const CONTRACT_TYPE_OPTIONS = [
  { type: "onboarding", label: "Main contract" },
  { type: "refinance", label: "Refinance" },
  { type: "addendum", label: "Addendum" },
  { type: "document", label: "Document" },
  { type: "one_off_contract", label: "Custom upload" },
] as const satisfies readonly { type: FlowType; label: string }[];

export type ContractFlowType = (typeof CONTRACT_TYPE_OPTIONS)[number]["type"];

/** Refinance, addendum, custom upload and document appear only when this actor may send that flow. */
export function contractTypeOptions(
  offered: readonly { type: FlowType }[]
): { type: ContractFlowType; label: string }[] {
  const available = new Set(offered.map((row) => row.type));
  return CONTRACT_TYPE_OPTIONS.filter(
    (option) => option.type === "onboarding" || available.has(option.type)
  );
}

export function selectedContractType(
  flowType: FlowType | null | undefined
): ContractFlowType | null {
  return CONTRACT_TYPE_OPTIONS.some((option) => option.type === flowType)
    ? (flowType as ContractFlowType)
    : null;
}

/** Contract template fields on the send card always use this label. */
export const DOCUMENT_TEMPLATE_LABEL = "Document Template";

/** The type dropdown replaces the send-main checkbox, so that contract stays included. */
export function sendCardConfig(dto: DraftEvaluation): FlowConfigValues {
  const config = inputConfig(dto);
  if (dto.flow_type === "onboarding") config.send_main = true;
  return config;
}

/**
 * Template picks the preview rejected as invalid: turned off since they were picked.
 * A custom upload is left to its own field, which follows its upload.
 */
export function rejectedTemplateKeys(err: unknown, dto: DraftEvaluation): string[] {
  if (!isAxiosError(err)) return [];
  const body = err.response?.data as MerchantFlowError | undefined;
  if (body?.error !== "invalid_config" || !body.errors) return [];
  const errors = body.errors;
  return uniqueSectionFields(dto.sections)
    .flatMap((section) => section.fields)
    .filter(
      (field) =>
        field.type === "template" &&
        field.key !== ONE_OFF_TEMPLATE_KEY &&
        errors[field.key] === "invalid"
    )
    .map((field) => field.key);
}

/** Contract fields on the send card. The type dropdown replaces the main-contract checkbox. */
export function contractControls(fields: readonly ConfigField[]): ConfigField[] {
  return fields
    .filter((field) => field.key !== "send_main")
    .map((field) =>
      field.type === "template"
        ? { ...field, label: DOCUMENT_TEMPLATE_LABEL, warning: undefined }
        : field
    );
}

/** Signed-contract (or similar) warning lifted off the template field to the section title. */
export function contractTemplateWarning(fields: readonly ConfigField[]): string | null {
  return fields.find((field) => field.type === "template" && field.warning)?.warning ?? null;
}

export interface PartitionedSendFields {
  contract: ConfigField[];
  /** Display-only terms field. The summary replaces the old modal button. */
  terms: ConfigField | null;
}

/**
 * Contract + terms for the send card. Disclosure and ACH addendum stay in the
 * draft config from defaults (automatic) but are not shown as editable fields.
 */
export function partitionSendFields(sections: DraftEvaluation["sections"]): PartitionedSendFields {
  const fields = uniqueSectionFields(sections).flatMap((section) => section.fields);
  return {
    contract: fields.filter((field) => CONTRACT_FIELD_KEYS.has(field.key)),
    terms: fields.find((field) => field.type === "terms_editor") ?? null,
  };
}

export function fieldEnabled(field: ConfigField, config: FlowConfigValues): boolean {
  if (!field.enabled_by) return true;
  return Boolean(config[field.enabled_by]);
}

export function currentValue(field: ConfigField, config: FlowConfigValues): unknown {
  return field.key in config ? config[field.key] : field.value;
}
